import { useNavigate } from "react-router-dom";

// Public marketing landing page. Lives at `/` on web. Purpose:
//   1. A public marketing surface Apple's App Store reviewer can
//      look at when validating that ShiftManager is a B2B SaaS
//      available to any agency (not a private tool for one org).
//   2. A conversion surface for cold traffic — surfaces the value
//      prop, features, and clear paths to "Start your agency" or
//      "Sign in" without dropping visitors straight into a login
//      form.
//
// The iOS Capacitor build skips this page entirely; native visitors
// go straight to Login because they've already installed the app
// with intent to sign in.

export function Landing() {
  const nav = useNavigate();

  return (
    <div className="min-h-full bg-white text-slate-900">
      {/* ---------- Top nav ---------- */}
      <header className="border-b border-slate-200">
        <div className="mx-auto max-w-6xl px-4 md:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldMark />
            <span className="text-base font-semibold tracking-tight">
              ShiftManager
            </span>
          </div>
          <nav className="flex items-center gap-2 md:gap-4 text-sm">
            <a
              href="#features"
              className="hidden md:inline text-slate-600 hover:text-slate-900"
            >
              Features
            </a>
            <a
              href="#who"
              className="hidden md:inline text-slate-600 hover:text-slate-900"
            >
              Who it's for
            </a>
            <a
              href="#security"
              className="hidden md:inline text-slate-600 hover:text-slate-900"
            >
              Security
            </a>
            <button
              onClick={() => nav("/login")}
              className="text-slate-700 hover:text-slate-900 font-medium px-2 py-1"
            >
              Sign in
            </button>
            <button
              onClick={() => nav("/start")}
              className="text-white bg-sky-700 hover:bg-sky-800 font-medium rounded-md px-3 py-1.5"
            >
              Start your agency
            </button>
          </nav>
        </div>
      </header>

      {/* ---------- Hero ---------- */}
      <section className="border-b border-slate-100">
        <div className="mx-auto max-w-6xl px-4 md:px-6 py-16 md:py-24 grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          <div>
            <div className="inline-block text-[11px] uppercase tracking-widest font-semibold text-sky-700 bg-sky-50 border border-sky-200 rounded px-2 py-1 mb-4">
              For law enforcement agencies
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight">
              Scheduling built for the people who run patrol.
            </h1>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              ShiftManager replaces the whiteboard, the group text,
              and the spreadsheet. Manage patrol shifts, time-off
              requests, on-call rotations, and training records —
              all in one place, on every device your agency uses.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                onClick={() => nav("/start")}
                className="text-white bg-sky-700 hover:bg-sky-800 font-semibold rounded-md px-5 py-2.5"
              >
                Start your agency
              </button>
              <button
                onClick={() => nav("/login")}
                className="text-slate-800 bg-white border border-slate-300 hover:bg-slate-50 font-semibold rounded-md px-5 py-2.5"
              >
                Sign in
              </button>
            </div>
            <p className="mt-4 text-xs text-slate-500">
              No contracts, no hardware, no IT install. Set up in
              under an hour.
            </p>
          </div>

          {/* Right-side visual — a mock schedule grid rendered in
            * CSS so we don't need image assets. Shows the shape of
            * what the app looks like without needing a screenshot. */}
          <div className="hidden md:block">
            <MockScheduleCard />
          </div>
        </div>
      </section>

      {/* ---------- Features ---------- */}
      <section id="features" className="border-b border-slate-100">
        <div className="mx-auto max-w-6xl px-4 md:px-6 py-16 md:py-24">
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              Everything an agency needs. Nothing it doesn't.
            </h2>
            <p className="mt-4 text-slate-600">
              Built with input from working supervisors. Every feature
              exists because a real agency asked for it.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Feature
              title="Patrol scheduling"
              body="Drag-and-drop pay-period view. Day, Evening, Night, and custom shifts per department. Automatic coverage math and OIC enforcement so a supervisor sees a hole before it becomes one."
            />
            <Feature
              title="Time off and leave"
              body="Officers request; supervisors approve with a note. Multi-level routing when policy requires an owner's sign-off. Custom fields per agency so state-specific requirements travel with the request."
            />
            <Feature
              title="On-call rotations"
              body="Detective on-call, SWAT call-out, ACO coverage — all in one place. Primary and secondary slots with radio and phone contact. Today's on-call always visible at the top of the schedule."
            />
            <Feature
              title="Training records"
              body="Track certifications, in-service hours, and renewal dates. Custom fields for state POST requirements. Filter, export, and share for accreditation reviews."
            />
            <Feature
              title="iOS, web, and notifications"
              body="Native iPhone app with push notifications. Full-featured web app for supervisors at their desks. Browser push for anyone working at their computer. Email backup on every alert."
            />
            <Feature
              title="Full audit log"
              body="Every shift change, time-off action, training update, and account modification is logged, sortable, and exportable. Built for records-retention compliance from day one."
            />
          </div>
        </div>
      </section>

      {/* ---------- Who it's for ---------- */}
      <section id="who" className="border-b border-slate-100 bg-slate-50">
        <div className="mx-auto max-w-6xl px-4 md:px-6 py-16 md:py-24">
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              Who uses it
            </h2>
            <p className="mt-4 text-slate-600">
              Any agency that runs 24/7 coverage. Municipal police,
              county sheriffs, state troopers, corrections,
              airport / port authority. If you have shifts and
              officers, it fits.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              "Police departments",
              "Sheriff's offices",
              "State troopers",
              "Corrections",
              "Airport / port authority",
              "Campus police",
              "Tribal police",
              "Public-safety hybrids",
            ].map((who) => (
              <div
                key={who}
                className="bg-white border border-slate-200 rounded-md px-3 py-3 text-sm font-medium text-slate-800"
              >
                {who}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Security / trust ---------- */}
      <section id="security" className="border-b border-slate-100">
        <div className="mx-auto max-w-6xl px-4 md:px-6 py-16 md:py-24">
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              Security first. By default.
            </h2>
            <p className="mt-4 text-slate-600">
              Your agency's data belongs to your agency. Period.
            </p>
          </div>

          <div className="mt-10 grid md:grid-cols-3 gap-4">
            <Feature
              title="Per-agency isolation"
              body="Every database row is scoped to a single agency via row-level security. Users from one agency can't read, write, or even see the existence of another agency's data."
            />
            <Feature
              title="Encrypted at rest and in transit"
              body="All connections use TLS. Push encryption via Apple's APNs and standard Web Push (RFC 8291) — the push services never see your notification contents."
            />
            <Feature
              title="Retention you control"
              body="When an agency cancels, data is archived and then permanently deleted within 90 days. Individual officer accounts can be soft-archived (schedule history preserved) or fully deleted on request."
            />
          </div>
        </div>
      </section>

      {/* ---------- Final CTA ---------- */}
      <section className="bg-slate-900 text-white">
        <div className="mx-auto max-w-6xl px-4 md:px-6 py-16 md:py-20 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
            Ready to run schedule the modern way?
          </h2>
          <p className="mt-4 text-slate-300 max-w-2xl mx-auto">
            Start your agency in a browser. Invite officers by
            email. First shift on the schedule in ten minutes.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => nav("/start")}
              className="text-white bg-sky-600 hover:bg-sky-500 font-semibold rounded-md px-5 py-2.5"
            >
              Start your agency
            </button>
            <button
              onClick={() => nav("/login")}
              className="text-slate-200 bg-transparent border border-slate-600 hover:bg-slate-800 font-semibold rounded-md px-5 py-2.5"
            >
              Sign in
            </button>
          </div>
        </div>
      </section>

      {/* ---------- Footer ---------- */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 md:px-6 py-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-sm text-slate-600">
          <div className="flex items-center gap-2">
            <ShieldMark small />
            <span>© 2026 ShiftManager</span>
          </div>
          <nav className="flex items-center gap-4">
            <a
              href="#/privacy"
              className="hover:text-slate-900"
            >
              Privacy
            </a>
            <a
              href="#/support"
              className="hover:text-slate-900"
            >
              Support
            </a>
            <button
              onClick={() => nav("/login")}
              className="hover:text-slate-900"
            >
              Sign in
            </button>
          </nav>
        </div>
      </footer>
    </div>
  );
}

// Small reusable feature card. Two lines: bold title, muted body.
function Feature({ title, body }: { title: string; body: string }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <h3 className="text-base font-semibold text-slate-900">{title}</h3>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed">
        {body}
      </p>
    </div>
  );
}

// Inline SVG shield "logo" — no image asset needed. Sized down when
// `small` is set for the footer.
function ShieldMark({ small }: { small?: boolean } = {}) {
  const size = small ? 18 : 24;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-sky-700"
      aria-hidden
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

// Faux schedule card rendered purely in CSS so we don't ship any
// image assets. Reads as "this is what the product looks like"
// without needing screenshots that would go stale every release.
function MockScheduleCard() {
  const officers = [
    { initials: "C.D.", radio: "A-1" },
    { initials: "M.G.", radio: "A-2" },
    { initials: "S.C.", radio: "A-3" },
    { initials: "T.M.", radio: "A-4" },
    { initials: "D.H.", radio: "A-5" },
  ];
  // Pre-computed cell states so the grid looks realistic. X = day
  // off (XXX in the app), TR = training, blank = on shift.
  const cells: Array<Array<"" | "X" | "TR" | "V">> = [
    ["", "X", "",   "",   "",   "",   "X"],
    ["", "",  "TR", "",   "",   "X",  "X"],
    ["", "",  "",   "TR", "TR", "",   ""],
    ["", "",  "",   "",   "",   "X",  ""],
    ["V","V", "",   "",   "",   "",   ""],
  ];
  const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 bg-sky-600 text-white text-sm font-semibold flex items-center justify-between">
        <span>Patrol · Day Shift</span>
        <span className="text-[10px] uppercase tracking-widest opacity-80">
          Week 1
        </span>
      </div>
      <div className="p-3 text-xs">
        <div className="grid grid-cols-[80px_repeat(7,minmax(0,1fr))] gap-1 text-slate-500 mb-1">
          <div />
          {days.map((d) => (
            <div key={d} className="text-center font-medium">
              {d}
            </div>
          ))}
        </div>
        {officers.map((o, i) => (
          <div
            key={o.initials}
            className="grid grid-cols-[80px_repeat(7,minmax(0,1fr))] gap-1 items-center"
          >
            <div className="flex items-baseline gap-1 text-slate-800">
              <span className="font-semibold">{o.initials}</span>
              <span className="text-slate-400 text-[10px] font-mono">
                #{o.radio}
              </span>
            </div>
            {cells[i].map((c, j) => (
              <div
                key={j}
                className={
                  "h-7 rounded border text-center leading-[1.7rem] text-[10px] font-semibold "
                  + (c === "X" ? "bg-slate-100 border-slate-200 text-slate-400"
                    : c === "TR" ? "bg-amber-50 border-amber-200 text-amber-700"
                    : c === "V" ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                    : "bg-white border-slate-100 text-transparent")
                }
              >
                {c === "X" ? "XXX" : c}
              </div>
            ))}
          </div>
        ))}
        <div className="grid grid-cols-[80px_repeat(7,minmax(0,1fr))] gap-1 mt-2 pt-2 border-t border-slate-100 text-[10px] uppercase tracking-wider text-slate-400">
          <div>Coverage</div>
          <div className="text-center text-emerald-700">4/3</div>
          <div className="text-center text-emerald-700">4/3</div>
          <div className="text-center text-emerald-700">4/3</div>
          <div className="text-center text-emerald-700">4/3</div>
          <div className="text-center text-emerald-700">4/3</div>
          <div className="text-center text-amber-700">3/3</div>
          <div className="text-center text-emerald-700">4/3</div>
        </div>
      </div>
    </div>
  );
}
